import React, { memo } from 'react'
import { Controller } from 'react-hook-form'
import { capitalize } from 'lodash'
import ControlledCheckboxGroupInput from 'transfix-form/components/ControlledCheckboxGroupInput/ControlledCheckboxGroupInput'
import { Radio } from 'transfix-ui/components/Radio/Radio'
import { Box, Typography } from 'transfix-ui/core'
import { titleCaseRuleName, getDecisionRules } from '../utils'
import { TDecision, TRules } from './types'
import { useRulesManualReviewModalForm } from './useRulesManualReviewModalForm'

interface IRulesCheckboxes {
  decision: TDecision
  methods: ReturnType<typeof useRulesManualReviewModalForm>
  rules: string[]
}

const RulesCheckboxes = memo(({ decision, methods, rules }: IRulesCheckboxes) => {
  const { control, watch } = methods || {}
  const selectedDecision = watch('decision')

  if (selectedDecision !== decision) {
    return null
  }

  const options = rules.map(rule => ({
    label: titleCaseRuleName(rule),
    id: rule,
  }))

  return (
    <Box display='grid' gap='16px' ml='32px' mt='8px'>
      <ControlledCheckboxGroupInput name='rules' options={options} control={control} />
    </Box>
  )
})

interface IDecisionRadio {
  decision: TDecision
  changeHandler: (newValue: string) => void
  value: string
  rules: TRules
  methods: ReturnType<typeof useRulesManualReviewModalForm>
}

const DecisionRadio = ({ changeHandler, decision, methods, rules, value }: IDecisionRadio) => {
  const rulesSource = getDecisionRules(decision, rules)

  if (!rulesSource.length) {
    return null
  }

  return (
    <div>
      <Radio
        checked={value === decision}
        onChange={() => changeHandler(decision)}
        value={decision}
        name={decision}
        label={capitalize(decision)}
      />
      <RulesCheckboxes decision={decision} methods={methods} rules={rulesSource} />
    </div>
  )
}

interface IRulesManualReviewModalForm {
  methods: ReturnType<typeof useRulesManualReviewModalForm>
  rules: TRules
}

export const RulesManualReviewModalForm = ({ methods, rules }: IRulesManualReviewModalForm) => {
  const { control } = methods || {}
  return (
    <form data-test='RulesManualReviewModalForm'>
      <Typography variant='subtitle1'>Select your decision</Typography>
      <Controller
        name='decision'
        control={control}
        defaultValue={null}
        render={({ onChange, value }) => {
          const changeHandler = (newValue: string) => {
            onChange(newValue)
          }

          return (
            <Box display='grid' gap='16px' my='16px'>
              <DecisionRadio
                decision='pass'
                changeHandler={changeHandler}
                value={value}
                rules={rules}
                methods={methods}
              />
              <DecisionRadio
                decision='fail'
                changeHandler={changeHandler}
                value={value}
                rules={rules}
                methods={methods}
              />
              <DecisionRadio
                decision='incomplete'
                changeHandler={changeHandler}
                value={value}
                rules={rules}
                methods={methods}
              />
            </Box>
          )
        }}
      />
    </form>
  )
}
